import { Component, OnInit, OnDestroy } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { faEye, faEdit, faSearch } from '@fortawesome/free-solid-svg-icons';
import { StorageKeys } from 'src/app/share/enums/storage.enum';
import { MasterTyreService } from 'src/app/share/services/master-tyre/master-tyre.service';
import { StorageService } from 'src/app/share/services/storage-service/storage.service';
import { Subject, Subscription } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { MessageService } from '../../share/sharedsubject';

@Component({
    selector: 'app-master-data-list',
    templateUrl: './master-data-list.component.html',
    styleUrls: ['./master-data-list.component.scss'],
    standalone: false
})
export class MasterDataListComponent implements OnInit, OnDestroy {
  private readonly searchSubject$ = new Subject<string>();
  private searchSubscription!: Subscription;
  readonly subscription: Subscription;

  protected faEye = faEye;
  protected faEdit = faEdit;
  protected faSearch = faSearch;
  tyreList: any[] = [];
  isAdmin: any;
  isCompanyUser: boolean = false;
  popupType:any;
  addTyreForm:FormGroup;
  showEditMakePopup:boolean=false;
  responseMsg:string;
  endOfResults: boolean=false;
  pageNumber: number=0;
  errorMessage: any;
  isToster: boolean = false;
  message: any;
  filters:any={
    page:0
  }
  isLoading  : boolean= false;
  loader : boolean = false;
  vehicleFormats: any = [
    { id: '4 wheeler', name: 'Car' },
    { id: 'LCV/Truck', name: 'LCV / Truck' },
    { id: '2 wheeler/3 wheeler', name: '2 Wheeler / 3 Wheeler' },
    { id: 'OTR/Tractor/Adv', name: 'Tractor / OTR / ADV' },
  ]

  visibleFields: any = {
    rimSize: { visible: false, required: false },
    aspectRatio: { visible: false, required: false },
    constructionType: { visible: false, required: false },
    crossSectionWidth: { visible: false, required: false },
    commercialTyre: { visible: false, required: false },
    profile: { visible: false, required: false },
  }

  constructor(
    private readonly formBuilder: FormBuilder,
    private readonly masterTyreService: MasterTyreService,
    private readonly router: Router,
    private readonly storageService: StorageService,
    private readonly messageService: MessageService
  ){
    this.addTyreForm = this.formBuilder.group({
      category: ['4 wheeler', Validators.required],
      rimSize: [''],
      aspectRatio: [''],
      constructionType: [''],
      speedSymbol: [''],
      crossSectionWidth: [''],
      minAmount: [''],
      maxAmount: [''],
      profitability: [''],
      commercialTyre: [''],
      profile: [''],
    });
    this.subscription = this.messageService.getMessage().subscribe((message: any) => {
      if (message) {
        this.isToster = true;
        this.message = message.text;
      }
    });
  }

  ngOnDestroy(): void {
    if (this.subscription) this.subscription.unsubscribe();
    if (this.searchSubscription) this.searchSubscription.unsubscribe();
  }

  ngOnInit(): void {
    let selfDetailsData = this.storageService.getDataFromLocalStorage(
      StorageKeys.SELF_DETAILS
    );
    if (selfDetailsData) {
      this.isAdmin = selfDetailsData.enrollmentType==='Admin';
      this.isCompanyUser = selfDetailsData.enrollmentType === 'Company';
    }

    this.searchSubscription = this.searchSubject$.pipe(
      debounceTime(300),
      distinctUntilChanged()
    ).subscribe((term: string) => {
      this.FilterList(term);
    });
    if(this.isAdmin){
      this.getTyreDetailsForAdmin();
      this.resetAllFields()
    }else{
      this.getTyreList();
    }
  }

  editData(tyresId:string){
    this.router.navigate(['/add-master-data'],{ queryParams : {tId:tyresId}})
  }

  saveTyreData() {
    this.isLoading = true;
    if (this.addTyreForm.invalid) {
      this.isLoading = true;
      return;
    }
    let category = this.addTyreForm.value.category;
    if (category === 'lvc') {
      const profile = this.addTyreForm.value.profile;
      const commercialTyre = this.addTyreForm.value.commercialTyre;
      if (profile && commercialTyre && profile.trim() !== '' && commercialTyre.trim() !== '') {
        this.message = 'Pattern does not match';
        this.isToster = true;
        this.isLoading = true;
        return;
      }
    }
    this.saveTyreDetails();
  }

  saveTyreDetails() {
    this.masterTyreService.saveTyreDetails(this.addTyreForm.value).subscribe({
      next: (res: any) => {
        this.isLoading = false;
        this.tyreList.unshift(res.data);
        this.addTyreForm.reset();
        this.showEditMakePopup = false;
        this.isToster = true;
        this.message = res.message;
        this.resetAllFields();
        this.responseMsg = '';
      },
      error: (error: any) => {
        this.responseMsg = error?.error?.error?.message;
        this.isLoading = false;
      }
    });
  }

  tyreAddFormPopup() {
    this.showEditMakePopup = true
  }

  closePopup() {
    this.responseMsg = '';
    this.addTyreForm.reset();
    this.showEditMakePopup = false;
    this.resetAllFields();
  }

  resetAllFields() {
    this.addTyreForm.controls['category'].setValue('4 wheeler');
    this.buildCarVisibilityFields();
  }

  getTyreList() {
    this.loader = true;
    const payload = {
      page: this.pageNumber,
      ...(this.filters.searchTerm && { searchTerm: this.filters.searchTerm })
    };
    this.masterTyreService.getTyreDetails(payload).subscribe({
      next: (res: any) => {
        this.loader = false;
        let newData = res.data || []
        this.tyreList = this.pageNumber == 0 ? newData : this.tyreList.concat(newData);
        if (res.data.length === 0) {
          this.endOfResults = true;
        }
      },
      error: (error) => {
        this.errorMessage = error?.error?.error?.message;
        this.loader = false;
      }
    });
  }

  onSearchInput(term: string) {
    this.searchSubject$.next(term);
  }

  FilterList(searchTerm: any) {
    this.endOfResults = false;
    this.pageNumber = 0;
    this.tyreList = [];
    this.filters.searchTerm = searchTerm || '';
    if(this.isAdmin){
      this.getTyreDetailsForAdmin();
    }else{
      this.getTyreList();
    }
  }

  getTyreDetailsForAdmin() {
    const payload = {
      page: this.pageNumber,
      ...(this.filters.searchTerm && { searchTerm: this.filters.searchTerm })
    };
    this.loader = true;
    this.masterTyreService.getTyreDetailsForAdmin(payload).subscribe({
      next: (res: any) => {
        this.loader = false;
        let newData = res.data || []
        this.tyreList = this.pageNumber==0 ? newData : this.tyreList.concat(newData);
        if (res?.data?.length === 0) {
          this.endOfResults = true;
        }
      },
      error: (error) => {
        this.errorMessage = error?.error?.error?.message;
        this.loader = false;
      }
    });
  }

  onScroll(){
    if(this.endOfResults || this.loader){
      return;
    }
    this.pageNumber++;
    if(this.isAdmin){
      this.getTyreDetailsForAdmin()
    }else{
    this.getTyreList();
  }
  }

  closeToster(event: any){
    this.isToster = false;
  }

  input(event: Event): void {
    const inputElement = event.target as HTMLInputElement;
    if (inputElement.value.length > 1) {
      inputElement.value = inputElement.value.slice(0, 1);
      this.addTyreForm.controls['constructionType'].setValue(inputElement.value);
    }
  }

  commertialInput(event: Event): void {
    const inputElement = event.target as HTMLInputElement;
    if (inputElement.value.length > 1) {
      inputElement.value = inputElement.value.slice(0, 1);
      this.addTyreForm.controls['commercialTyre'].setValue(inputElement.value);
    }
  }

  onChangeVehical(event: any) {
    this.responseMsg = '';
    let value = event.target.value;
    switch (value) {
      case '4 wheeler':
        this.buildCarVisibilityFields();
        break;
      case 'LCV/Truck':
        this.buildLCVVisibilityFields();
        break;
      case '2 wheeler/3 wheeler':
        this.buildTwoWheelerVisibilityFields();
        break;
      case 'OTR/Tractor/Adv':
        this.buildTractorVisibilityFields();
        break;
      default:
        break;
    }
  }



  buildCarVisibilityFields() {
    this.visibleFields = {
      rimSize: { visible: true, required: true },
      aspectRatio: { visible: true, required: true },
      constructionType: { visible: true, required: true },
      crossSectionWidth: { visible: true, required: true },
      commercialTyre: { visible: false, required: false },
      profile: { visible: false, required: false },
    }
    this.updateValidators()
  }

  buildLCVVisibilityFields() {
    this.visibleFields = {
      rimSize: { visible: true, required: true },
      aspectRatio: { visible: false, required: false },
      constructionType: { visible: true, required: true },
      crossSectionWidth: { visible: true, required: true },
      commercialTyre: { visible: true, required: false },
      profile: { visible: true, required: false },
    }
    this.updateValidators()
  }

  buildTwoWheelerVisibilityFields() {
    this.visibleFields = {
      rimSize: { visible: true, required: true },
      aspectRatio: { visible: false, required: false },
      constructionType: { visible: true, required: true },
      crossSectionWidth: { visible: true, required: true },
      commercialTyre: { visible: false, required: false },
      profile: { visible: true, required: false },
    }
    this.updateValidators()
  }

  buildTractorVisibilityFields() {
    this.visibleFields = {
      rimSize: { visible: true, required: true },
      aspectRatio: { visible: true, required: false },
      constructionType: { visible: true, required: true },
      crossSectionWidth: { visible: true, required: true },
      commercialTyre: { visible: false, required: false },
      profile: { visible: false, required: false },
    }
    this.updateValidators()
  }

  updateValidators() {
    Object.keys(this.visibleFields).forEach((field: string) => {
      const control = this.addTyreForm.get(field);

      if (!control) return;
      control.setErrors(null);
      if (this.visibleFields[field].required) {
        control.setValidators([Validators.required]);
      } else {
        control.clearValidators();
        control.setValue('');
      }
      control.markAsUntouched();
      control.updateValueAndValidity();
    });
    this.addTyreForm.updateValueAndValidity();
  }

}
